import { io } from 'socket.io-client';
import { useSelector } from 'react-redux';
import React, { useEffect, useState } from 'react';

import CircularProgress from '@mui/material/CircularProgress';
import { Dialog, Box, Grid, Button, Typography } from '@mui/material';

import { snackAlertData } from 'utils/constants';
import { ACTIVITY_LABELS } from './utils/history.constants';
import { handleErrorAlert } from '../../utils/handleErrorAlert';
import { CircleLoading, Modal, SnackBarAlert } from '../../shared';
import { handleSuccessAlert } from '../../utils/handleSuccessAlert';
import { useHistoryService } from '../../store/services/history.service';
import { historyModalStyles } from 'components/History/styles/HistoryDropDown.style';

const pageSize = 3;
const TOKEN = process.env.REACT_APP_SOCKET_TOKEN;
const SOCKET_URL = process.env.REACT_APP_SOCKET_URL;

const HistoryActionModal = ({ open, handleClose }) => {
    const classes = historyModalStyles();
    const companyId = useSelector((state) => state?.company?.data?.id);
    const { getHistoryActions, revertHistoryAction } = useHistoryService();

    const [page, setPage] = useState(1);
    const [count, setCount] = useState(0);
    const [actions, setActions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [revertingId, setRevertingId] = useState(null);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [selectedAction, setSelectedAction] = useState(null);
    const [alertData, setAlertData] = useState(snackAlertData);

    const handleGetHistoryAction = async (pageNumber) => {
        try {
            setLoading(true);
            const response = await getHistoryActions({ companyId, page: pageNumber, pageSize });
            const revertingAction = response?.results?.find(
                (action) => action.status == 'reverting',
            );
            if (revertingAction) {
                setRevertingId(revertingAction?.id);
            }
            setActions(response?.results || []);
            setCount(response?.count || 0);
        } catch (error) {
        } finally {
            setLoading(false);
        }
    };

    const handleConfirmYes = async () => {
        setConfirmOpen(false);
        await handleRevert(selectedAction);
        setSelectedAction(null);
    };

    const handleConfirmNo = () => {
        setConfirmOpen(false);
        setSelectedAction(null);
    };

    const handleRevertClick = (action) => {
        setSelectedAction(action);
        setConfirmOpen(true);
    };

    const handleRevert = async (action) => {
        try {
            setRevertingId(action.id);
            const response = await revertHistoryAction({
                companyId,
                userActivityId: action.id,
            });
            handleSuccessAlert(setAlertData, response.message);
        } catch (error) {
            handleErrorAlert(setAlertData, 'Failed to revert action. Please try again.');
        }
    };

    const formatDate = (dateStr) => new Date(dateStr).toLocaleString();

    const handlePageChange = async (pageNumber) => {
        setPage(pageNumber);
        setRevertingId(null);
        await handleGetHistoryAction(pageNumber);
    };

    useEffect(() => {
        if (open && companyId) {
            handleGetHistoryAction(page);
        }
    }, [open, companyId]);

    useEffect(() => {
        if (!companyId) return;

        const socket = io(SOCKET_URL, {
            transports: ['websocket'],
            path: '/api/socket/',
            query: {
                token: TOKEN,
            },
        });

        const eventName = `user-activity/${companyId}/`;

        socket.off(eventName);
        socket.on(eventName, (data) => {
            if (data.status == 'reverted') {
                handleSuccessAlert(setAlertData, 'Activity reverted successfully');
                setRevertingId(null);
                let newPage = page;
                if (actions.length === 1 && page > 1) {
                    newPage = page - 1;
                    setPage(newPage);
                }
                handleGetHistoryAction(newPage);
            }
        });

        socket.on('connect_error', (error) => {
            console.error('Connection error:', error);
        });

        return () => {
            socket.disconnect();
        };
    }, [companyId]);

    return (
        <Modal
            fullWidth
            open={open}
            maxWidth="sm"
            showFooter={false}
            handleClose={handleClose}
            title="User Activity History"
            PaperProps={{ sx: classes.mainModalPaper }}
        >
            <Box sx={classes.boxSize}>
                {loading ? (
                    <CircleLoading />
                ) : (
                    <Grid container spacing={2}>
                        {actions.length === 0 ? (
                            <Box sx={classes.noUserActivityWrapper}>
                                <Typography textAlign="center">No user activity found.</Typography>
                            </Box>
                        ) : (
                            actions.map((action) => (
                                <Grid item xs={12} key={action.id} sx={classes.historyList}>
                                    <Box>
                                        <Typography variant="body1" sx={classes.activityTitle}>
                                            {ACTIVITY_LABELS[action.action] || action.action}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            {action.first_name} {action.last_name}
                                            {action.inventory_file_name && (
                                                <> — {action.inventory_file_name}</>
                                            )}
                                            {action.transactions_file_name && (
                                                <> — {action.transactions_file_name}</>
                                            )}
                                            {action.remittance_file_name && (
                                                <> — {action.remittance_file_name}</>
                                            )}
                                            {action.invoice_file_name && (
                                                <> — {action.invoice_file_name}</>
                                            )}
                                            {action.aggregated_count > 0 && (
                                                <> (x{action.aggregated_count})</>
                                            )}
                                        </Typography>
                                        <Typography variant="caption" color="text.disabled">
                                            {formatDate(action.created_at)}
                                        </Typography>
                                    </Box>
                                    <Button
                                        variant="outlined"
                                        color="error"
                                        disabled={!!revertingId}
                                        onClick={() => handleRevertClick(action)}
                                    >
                                        {revertingId === action.id ? 'Reverting...' : 'Revert'}

                                        {revertingId === action.id && (
                                            <CircularProgress
                                                color="inherit"
                                                size={15}
                                                sx={classes.revertLoader}
                                            />
                                        )}
                                    </Button>
                                </Grid>
                            ))
                        )}

                        {count > pageSize && (
                            <Grid item xs={12} sx={classes.pagination}>
                                <Button
                                    disabled={page === 1}
                                    onClick={() => handlePageChange(page - 1)}
                                >
                                    Previous
                                </Button>
                                <Button
                                    disabled={page * pageSize >= count}
                                    onClick={() => handlePageChange(page + 1)}
                                >
                                    Next
                                </Button>
                            </Grid>
                        )}
                    </Grid>
                )}

                <SnackBarAlert alertData={alertData} setAlertData={setAlertData} />

                <Dialog
                    open={confirmOpen}
                    onClose={handleConfirmNo}
                    PaperProps={{ sx: classes.confirmDialog }}
                >
                    <Box p={3}>
                        <Typography variant="h6" gutterBottom>
                            What it means to “revert”:
                        </Typography>
                        <Typography variant="body1" gutterBottom>
                            The revert option allows you to undo user activities such as transaction
                            or purchase uploads, which deletes the specific file's data and removes
                            its reconciliation. Please note that once an activity is reverted, all
                            associated data is permanently deleted and cannot be restored. Are you sure you want to continue?
                        </Typography>
                        <Box mt={4} display="flex" justifyContent="flex-end" gap={1}>
                            <Button variant="contained" color="error" onClick={handleConfirmYes}>
                                Yes, Continue
                            </Button>

                            <Button variant="outlined" onClick={handleConfirmNo}>
                                No, Cancel
                            </Button>

                        </Box>
                    </Box>
                </Dialog>
            </Box>
        </Modal>
    );
};

export default HistoryActionModal;
